/**
 * RowActionsTrigger — the ⋯ button of a patients-area row menu (patients list, appointments, invoices, receipts)
 * with an instant tooltip that names it ("Actions for <row>"). Antonin, 15 Oct: "ok" to an instant tooltip on the
 * row ⋯ buttons; the same no-delay, no-fade tooltip as the rest of the app (the bridge's tooltip block).
 *
 * The DS popover trigger stays the outer element (its `data-slot`, click, aria-expanded and anchoring are
 * unchanged); the DS `TooltipTrigger` is composed into it through `render`. Must sit inside the row's `AnchoredPopover`.
 * The accessible name stays the button's own `aria-label`; the tooltip is the visible name of the same control.
 */
import type { ReactNode } from 'react';
import {
  AnchoredPopoverTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@scripsteam/scrips-signal-ds';
import { cn } from '@/lib/utils';

export interface RowActionsTriggerProps {
  /** The button's accessible name (unchanged by the tooltip), e.g. "Patient actions". */
  ariaLabel: string;
  /** The tooltip text, e.g. "Actions for Sara Rahman". */
  tooltip: string;
  /** The ⋯ glyph. */
  children: ReactNode;
}

export function RowActionsTrigger({ ariaLabel, tooltip, children }: RowActionsTriggerProps) {
  return (
    // delay 0 / closeDelay 0: opens and closes at once (design log 13 Oct, Rule "tooltips").
    <TooltipProvider delay={0} closeDelay={0}>
      <Tooltip>
        <AnchoredPopoverTrigger
          render={
            <TooltipTrigger
              render={
                <button
                  type="button"
                  aria-label={ariaLabel}
                  data-row-action="menu"
                  className={cn(
                    'inline-flex h-8 w-8 items-center justify-center rounded-md text-text-muted',
                    'hover:bg-grey-100 hover:text-text-primary',
                  )}
                />
              }
            />
          }
        >
          {children}
        </AnchoredPopoverTrigger>
        <TooltipContent side="top">{tooltip}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
